import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Check, Plus, Tag as TagIcon } from 'lucide-react';
import { cn } from '../../../lib/utils';
import {
  Command,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
} from '../../../components/ui/command';
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from '../../../components/ui/popover';
import { InfoTooltip } from '../common/InfoTooltip';
import { TagPageLink } from './TagPageLink';
import { useEntityTypeMeta } from './useEntityTypeMeta';
import { useTagSuggestions } from './useTagSuggestions';
import type { TagSuggestion } from '@/generated/api/models/tagSuggestion';

export interface TagPickerProps {
  /** Tag ids already selected — these are excluded from the suggestions. */
  excludeIds?: string[];
  /** {module}.{entity} string for personalization. */
  entityType?: string;
  /** Called when a known tag is picked. */
  onPick: (suggestion: TagSuggestion) => void;
  /**
   * Called when the user types a name that doesn't match an existing tag and
   * confirms (Enter or click "Create"). Receives the typed name.
   */
  onCreate?: (name: string) => void;
  /**
   * Render as a controlled popover anchored on the given trigger.
   * Default: uses a small "Add tag" trigger button.
   */
  triggerSlot?: React.ReactNode;
  /** Initial open state for the popover. */
  defaultOpen?: boolean;
  className?: string;
}

function formatRelative(iso: string | null): string | null {
  if (!iso) return null;
  const date = new Date(iso);
  const seconds = Math.max(0, (Date.now() - date.getTime()) / 1000);
  if (seconds < 60) return `${Math.floor(seconds)}s`;
  if (seconds < 3600) return `${Math.floor(seconds / 60)}m`;
  if (seconds < 86400) return `${Math.floor(seconds / 3600)}h`;
  return `${Math.floor(seconds / 86400)}d`;
}

/**
 * Autocomplete dropdown for picking (or creating) a tag.
 *
 * Suggestions are ranked server-side. Each row shows a color swatch, the tag
 * name, a subtle "yours" dot when the current user has used it before, and
 * an InfoTooltip with personalized usage stats + description (per design
 * doc §Frontend Components).
 *
 * When the caller names an `entityType`, the rows arrive in two runs — tags
 * already used on that entity type, then the rest — and this splits them under
 * their own headings. Tags are a single tenant-wide namespace with no per-type
 * scoping, so "already used on a contact" is the only signal that a tag belongs
 * on the contact you are tagging; naming it is what makes a long shared list
 * usable. The split mirrors the server's own rule (`entity_type_usage_count > 0`, which
 * only means "used on THIS entity type" once an entity type was queried), so
 * the two never disagree.
 */
export function TagPicker({
  excludeIds,
  entityType,
  onPick,
  onCreate,
  triggerSlot,
  defaultOpen = false,
  className,
}: TagPickerProps) {
  const { t } = useTranslation('common');
  const [open, setOpen] = useState(defaultOpen);
  const [query, setQuery] = useState('');

  const { suggestions, loading } = useTagSuggestions({
    prefix: query,
    entityType,
    enabled: open,
  });

  const entityTypeMeta = useEntityTypeMeta();

  const excludeSet = new Set(excludeIds ?? []);
  const filtered = suggestions.filter((s) => !excludeSet.has(s.id));

  // Without an entityType there is nothing to be relevant TO, so everything
  // falls in one unlabelled run — which is also what the admin governance UI
  // wants. `entity_type_usage_count` counts ALL usages in that case, so testing it
  // here would split the list on a distinction that doesn't exist.
  const relevant = entityType ? filtered.filter((s) => s.entity_type_usage_count > 0) : [];
  const others = entityType
    ? filtered.filter((s) => s.entity_type_usage_count === 0)
    : filtered;
  // A heading over the only section on screen is noise, not orientation.
  const showHeadings = relevant.length > 0 && others.length > 0;

  const trimmed = query.trim();
  const exactMatch = filtered.find(
    (s) => s.name.trim().toLowerCase() === trimmed.toLowerCase(),
  );
  const canCreate = !!onCreate && trimmed.length > 0 && !exactMatch;

  const handlePick = (suggestion: TagSuggestion) => {
    onPick(suggestion);
    setQuery('');
    setOpen(false);
  };

  const handleCreate = () => {
    if (!canCreate || !onCreate) return;
    onCreate(trimmed);
    setQuery('');
    setOpen(false);
  };

  const renderRow = (suggestion: TagSuggestion) => {
    const lastUsed = formatRelative(suggestion.user_last_used);
    const tooltipParts: string[] = [];
    if (lastUsed) {
      tooltipParts.push(
        t('app.youLastUsedAgo', {
          defaultValue: 'You used this {{ago}} ago',
          ago: lastUsed,
        }),
      );
    }
    if (suggestion.entity_type_usage_count) {
      tooltipParts.push(
        t('app.usedNxHere', {
          defaultValue: 'Used {{n}}× here',
          n: suggestion.entity_type_usage_count,
        }),
      );
    }
    if (suggestion.total_count) {
      tooltipParts.push(
        t('app.usedNxTotal', {
          defaultValue: '{{n}}× total',
          n: suggestion.total_count,
        }),
      );
    }
    if (suggestion.description) {
      tooltipParts.push(suggestion.description);
    }
    const tooltipText = tooltipParts.join(' · ');
    return (
      <CommandItem
        key={suggestion.id}
        value={suggestion.id}
        onSelect={() => handlePick(suggestion)}
        className="flex items-center gap-2"
      >
        <span
          aria-hidden
          className="inline-block h-3 w-3 flex-shrink-0 rounded-full border border-border"
          style={
            suggestion.color ? { backgroundColor: suggestion.color } : undefined
          }
        />
        <span className="flex-1 truncate">{suggestion.name}</span>
        {suggestion.user_last_used && (
          <span
            aria-hidden
            className="h-1.5 w-1.5 rounded-full bg-primary"
            title={t('app.yoursTag', { defaultValue: 'You used this tag' })}
          />
        )}
        {tooltipText && <InfoTooltip content={tooltipText} side="right" />}
        {/*
          The row's stats tooltip above is a lossy preview of the tag's own
          page; this is the way to it. Without it the picker — the surface
          where a user meets a tag most often — offered no route to the tag
          page at all.
        */}
        <TagPageLink tag={suggestion} />
      </CommandItem>
    );
  };

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        {triggerSlot ?? (
          <button
            type="button"
            className={cn(
              'inline-flex h-6 items-center gap-1 rounded-full border border-dashed border-border bg-background px-2 text-xs text-muted-foreground hover:bg-accent',
              className,
            )}
          >
            <TagIcon className="h-3 w-3" />
            {t('app.addTag', { defaultValue: 'Add tag' })}
          </button>
        )}
      </PopoverTrigger>
      <PopoverContent className="w-72 p-0" align="start">
        <Command shouldFilter={false}>
          <CommandInput
            value={query}
            onValueChange={setQuery}
            placeholder={t('app.searchOrCreateTag', { defaultValue: 'Search or create…' })}
          />
          <CommandList>
            {loading && (
              <div className="p-2 text-xs text-muted-foreground">
                {t('app.loading', { defaultValue: 'Loading…' })}
              </div>
            )}
            {!loading && filtered.length === 0 && !canCreate && (
              <CommandEmpty>
                {t('app.noTagsFound', { defaultValue: 'No tags found.' })}
              </CommandEmpty>
            )}
            {relevant.length > 0 && (
              <CommandGroup
                heading={
                  showHeadings && entityType
                    ? t('app.tagsUsedOnEntity', {
                        defaultValue: 'Used on {{entity}}',
                        entity: entityTypeMeta(entityType).plural,
                      })
                    : undefined
                }
              >
                {relevant.map(renderRow)}
              </CommandGroup>
            )}
            {others.length > 0 && (
              <CommandGroup
                heading={
                  showHeadings
                    ? t('app.otherTags', { defaultValue: 'Other tags' })
                    : undefined
                }
              >
                {others.map(renderRow)}
              </CommandGroup>
            )}
            {canCreate && (
              <CommandGroup>
                <CommandItem
                  value={`__create_${trimmed}`}
                  onSelect={handleCreate}
                  className="flex items-center gap-2"
                >
                  <Plus className="h-4 w-4" />
                  <span className="flex-1 truncate">
                    {t('app.createTagNamed', {
                      defaultValue: 'Create "{{name}}"',
                      name: trimmed,
                    })}
                  </span>
                  <Check className="h-4 w-4 opacity-50" />
                </CommandItem>
              </CommandGroup>
            )}
          </CommandList>
        </Command>
      </PopoverContent>
    </Popover>
  );
}
